import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, Loader2, ArrowLeft, AlertCircle } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConsultationSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | pending | expired | error

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return undefined;
    }
    let cancelled = false;
    let timer;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      if (attempts >= 8) {
        setStatus("pending");
        return;
      }
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus("paid");
        } else if (data.status === "expired") {
          setStatus("expired");
        } else {
          timer = setTimeout(poll, 2000);
        }
      } catch {
        if (!cancelled) timer = setTimeout(poll, 2500);
      }
    };

    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [sessionId]);

  return (
    <div className="App bg-white min-h-screen" data-testid="consultation-success-page">
      <Header />
      <main className="pt-36 pb-24 min-h-[80vh]" style={{ background: "linear-gradient(180deg,#FFE5D9 0%,#FFFFFF 60%,#E8DCF3 100%)" }}>
        <div className="max-w-2xl mx-auto px-6 lg:px-10 text-center">
          {status === "checking" && (
            <>
              <div className="w-16 h-16 rounded-full bg-white shadow flex items-center justify-center mx-auto text-[var(--gg-primary)]">
                <Loader2 className="animate-spin" size={26} />
              </div>
              <h1 className="font-serif text-4xl text-[var(--gg-deep)] mt-6 font-semibold">
                Confirming your payment…
              </h1>
              <p className="text-[var(--gg-text)] mt-3">This usually takes just a moment.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <div className="w-16 h-16 rounded-full bg-[var(--gg-primary)]/15 flex items-center justify-center mx-auto text-[var(--gg-primary)]">
                <Check size={30} />
              </div>
              <h1 className="font-serif text-4xl md:text-5xl text-[var(--gg-deep)] mt-6 font-semibold">
                Your consultation is booked.
              </h1>
              <p className="text-[var(--gg-text)] mt-4 max-w-md mx-auto leading-relaxed">
                Thank you. Your payment has been received and your slot is
                confirmed. A confirmation email is on its way, and a care
                coordinator will reach out shortly before your appointment.
              </p>
              <Link
                to="/"
                data-testid="success-back-home"
                className="inline-flex items-center gap-2 mt-8 bg-[var(--gg-primary)] hover:bg-[var(--gg-primary-dark)] text-white rounded-full px-6 py-3 text-sm"
              >
                <ArrowLeft size={16} /> Back home
              </Link>
            </>
          )}
          {status === "pending" && (
            <>
              <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto text-yellow-700">
                <AlertCircle size={26} />
              </div>
              <h1 className="font-serif text-4xl text-[var(--gg-deep)] mt-6 font-semibold">
                We&apos;re still confirming.
              </h1>
              <p className="text-[var(--gg-text)] mt-3">
                Your payment is processing. You&apos;ll receive an email
                confirmation as soon as it clears — usually within a few
                minutes. No further action needed.
              </p>
            </>
          )}
          {status === "expired" && (
            <>
              <h1 className="font-serif text-4xl text-[var(--gg-deep)] font-semibold">
                The checkout session expired.
              </h1>
              <p className="text-[var(--gg-text)] mt-3">
                Please return to the booking form and try again.
              </p>
              <Link to="/#contact" className="inline-flex items-center gap-2 mt-8 bg-[var(--gg-primary)] hover:bg-[var(--gg-primary-dark)] text-white rounded-full px-6 py-3 text-sm">
                <ArrowLeft size={16} /> Back to booking
              </Link>
            </>
          )}
          {status === "error" && (
            <>
              <h1 className="font-serif text-4xl text-[var(--gg-deep)] font-semibold">
                We couldn&apos;t confirm this session.
              </h1>
              <p className="text-[var(--gg-text)] mt-3">
                If you were charged, please email <a className="text-[var(--gg-primary)]" href="mailto:info@caregracefulguidance.com">info@caregracefulguidance.com</a> and we&apos;ll sort it right away.
              </p>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
